import { Location, useLocation } from "@remix-run/react";
import { useEffect, useState } from "react";
import { useNavigateTo } from "~/shared/hooks";
import { useClickOutside } from "~/shared/hooks/use-click-outside";
import { MenusType, NavLinkType } from "../types";

export const handleRoute = (links: NavLinkType[], currentLocation: string) => {
  let headerItem = links.find((item) =>
    item.baseURL
      ? currentLocation.includes(item.baseURL) || item.href === currentLocation
      : item.href === currentLocation || item.href === `/${currentLocation}`,
  );

  if (!headerItem)
    headerItem = links.find((item) =>
      item.nestedURls ? item.nestedURls.includes(currentLocation) : false,
    );

  return headerItem?.label;
};

export function useHandleHeaderRoute(
  location: Location,
  navLinks: NavLinkType[],
) {
  const [activeLink, setActiveLink] = useState(() =>
    handleRoute(navLinks, location.pathname.substring(3)),
  );

  useEffect(() => {
    setActiveLink(handleRoute(navLinks, location.pathname.substring(3)));
  }, [location.pathname, navLinks]);

  return {
    handleRoute,
    activeLink,
    setActiveLink,
  };
}

export function useHandleHeaderMenu(location: Location, menus: MenusType) {
  const [activeMenu, setActiveMenu] = useState("");
  const MenuComponent = activeMenu && menus[activeMenu as keyof typeof menus];

  useEffect(() => {
    setActiveMenu("");
  }, [location.pathname]);

  return {
    activeMenu,
    setActiveMenu,
    MenuComponent,
  };
}

export function useHeaderNavLinks(navLinks: NavLinkType[], menus: MenusType) {
  const navigate = useNavigateTo();
  const location = useLocation();
  const { activeLink, setActiveLink } = useHandleHeaderRoute(
    location,
    navLinks,
  );
  const { activeMenu, setActiveMenu, MenuComponent } = useHandleHeaderMenu(
    location,
    menus,
  );
  const menuRef = useClickOutside(() => setActiveMenu(""));

  const currentLocation = location.pathname.substring(3);

  const handleNavigateChange = (link: NavLinkType) => {
    if (link.label === activeMenu) {
      setActiveMenu("");
      return;
    }
    if (link.href) {
      setActiveLink(link.label);
      setActiveMenu("");
      navigate(link.href, { viewTransition: true });
    } else {
      setActiveMenu(link.label);
    }
  };

  return {
    activeLink,
    setActiveLink,
    activeMenu,
    setActiveMenu,
    handleNavigateChange,
    currentLocation,
    labelName: activeMenu ? activeMenu : activeLink,
    MenuComponent,
    menuRef,
  };
}
